import React, { useCallback, useContext, useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { ContextMenu, Body, CircleIcon, PdfModal, ImagesCarousel, FlexColumn } from '@ubeya/shared-web/components';
import { ReactComponent as BasePhoneMenuIcon } from '@ubeya/shared-web/assets/chat.svg';
import { ReactComponent as OkIcon } from '@ubeya/shared-web/assets/confirmed.svg';
import { ReactComponent as ViewIcon } from '@ubeya/shared-web/assets/eye.svg';
import { ReactComponent as DownloadIcon } from '@ubeya/shared-web/assets/import.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { isDocumentFieldValue } from './profileSidebarUtils';

const isPdfLink = (link) => link && /\.pdf($|\?)/i.test(link);
const isImageLink = (link) => link && /\.(jpe?g|png|gif|webp|bmp)($|\?)/i.test(link);
const isPreviewable = (link) => isPdfLink(link) || isImageLink(link);

export const Chips = styled(FlexColumn)`
  gap: 8px;
  flex-direction: ${({ fixedWidth }) => (fixedWidth ? 'column' : 'row')};
  flex-wrap: ${({ fixedWidth }) => (fixedWidth ? 'nowrap' : 'wrap')};
`;

const DocumentItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.primaryLight};
  width: ${({ fixedWidth }) => (fixedWidth ? '100%' : 'fit-content')};
  max-width: 100%;
  min-width: 0;
  gap: 4px;
`;

const DocumentItemTitle = styled(Body)`
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DocumentItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PhoneMenuIcon = styled(BasePhoneMenuIcon)`
  margin-left: 10px;
`;

const PhoneContextMenu = styled(ContextMenu)`
  bottom: 0;
  right: 0;
  > div:last-child {
    > div {
      padding: 8px;
    }
  }
`;

const DetailBlockContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const DetailBlockTextWrapper = styled.div`
  width: fit-content;
`;

export const Chip = styled.span`
  display: inline-block;
  background: ${({ theme, $isColored }) => ($isColored ? theme.colors.primaryLight : theme.colors.grey1)};
  color: ${({ theme }) => theme.colors.black};
  padding: 8px;
  border-radius: 4px;
  line-height: 20px;
  word-break: break-word;
  cursor: ${({ $disableClick }) => !$disableClick && 'pointer'};
  width: 100%;

  svg {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
  }
`;

export const DetailsBlockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

export const DetailBlockText = styled(Body)`
  display: flex;
  gap: 4px;
  justify-content: space-between;
  color: ${({ $isLongDetail, theme }) => $isLongDetail && theme.colors.grey4};
`;
const DetailBlockContentWraper = styled.div`
  gap: 8px;
  display: flex;
  align-items: flex-start;
`;

export const DetailsBlockChip = styled(Chip)`
  display: flex;
  flex-direction: column;
  padding: 8px;
`;

const isLongDetail = (text) => (text ? String(text) : '').split(/[, ]+/).length > 1;

const DocumentLinkItem = ({ item, fixedWidth, onViewClick }) => {
  const { t } = useContext(ConfigContext);
  const previewable = isPreviewable(item?.link);
  const onView = useCallback(
    () => (previewable ? onViewClick?.(item) : item?.link && window.open(item.link, '_blank')),
    [previewable, item, onViewClick]
  );
  const onDownload = useCallback(() => item?.link && window.open(item.link, '_blank'), [item?.link]);

  return (
    <DocumentItemRow fixedWidth={fixedWidth}>
      <DocumentItemTitle title={item?.title || ''}>{item?.title || ''}</DocumentItemTitle>
      {item?.link && (
        <DocumentItemActions>
          {previewable && <CircleIcon icon={ViewIcon} tooltip={t('view')} onClick={onView} />}
          <CircleIcon icon={DownloadIcon} tooltip={t('download')} onClick={onDownload} />
        </DocumentItemActions>
      )}
    </DocumentItemRow>
  );
};

const DocumentLinksBlock = ({ value, fixedWidth, onPreviewOpenChange }) => {
  const [openPdf, setOpenPdf] = useState(null);
  const [openImageIndex, setOpenImageIndex] = useState(null);

  const isPreviewOpen = !!openPdf || (openImageIndex !== undefined && openImageIndex !== null);
  useEffect(() => {
    onPreviewOpenChange?.(isPreviewOpen);
    return () => {
      onPreviewOpenChange?.(false);
    };
  }, [isPreviewOpen, onPreviewOpenChange]);

  const imageViews = useMemo(
    () => value?.filter((item) => isImageLink(item?.link)).map((item) => ({ source: item.link })) ?? [],
    [value]
  );

  const onViewClick = useCallback(
    (item) => {
      if (!item?.link) return;
      if (isPdfLink(item.link)) {
        setOpenPdf(item.link);
        return;
      }
      if (isImageLink(item.link)) {
        const imageItems = value.filter((i) => isImageLink(i?.link));
        const idx = imageItems.findIndex((i) => i.link === item.link);
        setOpenImageIndex(idx >= 0 ? idx : null);
      }
    },
    [value]
  );

  return (
    <>
      {value.map((item, index) => (
        <DocumentLinkItem key={index} item={item} fixedWidth={fixedWidth} onViewClick={onViewClick} />
      ))}
      {openPdf && <PdfModal url={openPdf} onClose={() => setOpenPdf(null)} />}
      <ImagesCarousel views={imageViews} currentIndex={openImageIndex} onClose={() => setOpenImageIndex(null)} />
    </>
  );
};

export const DetailRow = ({
  label,
  fixedWidth,
  value,
  options,
  Icon,
  onClick,
  isInline = false,
  isCompliance = false,
  isAttachment = false,
  onPreviewOpenChange
}) => (
  <DetailsBlockWrapper>
    {Array.isArray(value) ? (
      <>
        <DetailBlockText>{label}</DetailBlockText>
        <Chips fixedWidth={fixedWidth}>
          {isDocumentFieldValue(value) ? (
            <DocumentLinksBlock value={value} fixedWidth={fixedWidth} onPreviewOpenChange={onPreviewOpenChange} />
          ) : (
            value.map((item, index) => (
              <Chip
                fixedWidth={fixedWidth}
                $disableClick={!item.link}
                onClick={() => item?.link && window.open(item.link, '_blank')}
                $isColored
                key={index}>
                <DetailBlockText>{item?.title}</DetailBlockText>
              </Chip>
            ))
          )}
        </Chips>
      </>
    ) : (
      <>
        <DetailsBlockWrapper>
          <Chips fixedWidth={fixedWidth}>
            <DetailsBlockChip $disableClick>
              {!isInline && isLongDetail(value?.title) ? (
                <DetailBlockContentWrapper>
                  <DetailBlockTextWrapper>
                    <DetailBlockText>{label}</DetailBlockText>
                    <DetailBlockText $isLongDetail>
                      {Array.isArray(value?.title) ? value?.title.join(', ') : value?.title}
                    </DetailBlockText>
                  </DetailBlockTextWrapper>
                  {isAttachment && <OkIcon />}
                </DetailBlockContentWrapper>
              ) : (
                <DetailBlockText>
                  <DetailBlockContentWraper>
                    {Icon && !isCompliance && <Icon onClick={onClick} />}
                    {label}
                    {!isCompliance && ': '}
                    {value?.title}
                  </DetailBlockContentWraper>
                  {options && (
                    <PhoneContextMenu options={options}>
                      <PhoneMenuIcon />
                    </PhoneContextMenu>
                  )}
                  {isAttachment && <OkIcon />}
                  {Icon && isCompliance && <Icon onClick={onClick} />}
                </DetailBlockText>
              )}
            </DetailsBlockChip>
          </Chips>
        </DetailsBlockWrapper>
      </>
    )}
  </DetailsBlockWrapper>
);
